import { useEffect, useState, useCallback } from 'react';

const PINNED_KEY = 'home:pinnedApps:v1';
const SYNC_EVENT = 'pinnedAppsChanged';
const MAX_PINNED = 20;

const readPinned = (): string[] => {
  try {
    const raw = localStorage.getItem(PINNED_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

/**
 * Single source of truth for the pinned-apps watch list. Returns the current
 * list plus a `togglePin` function and reacts live to changes from any other
 * component in the same tab (custom event) or other tabs (storage event), so
 * pinning a row in the activity feed immediately updates the Home overview
 * panel without a refresh.
 */
export function usePinnedApps() {
  const [pinned, setPinned] = useState<string[]>(readPinned);

  useEffect(() => {
    const syncFromStorage = () => setPinned(readPinned());

    // Cross-tab updates fire `storage`; same-tab updates use our custom event
    // since the `storage` event intentionally does NOT fire on the writing tab.
    const onStorage = (e: StorageEvent) => {
      if (e.key && e.key !== PINNED_KEY) return;
      syncFromStorage();
    };
    const onCustom = () => syncFromStorage();

    window.addEventListener('storage', onStorage);
    window.addEventListener(SYNC_EVENT, onCustom);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(SYNC_EVENT, onCustom);
    };
  }, []);

  const togglePin = useCallback((host: string) => {
    setPinned(prev => {
      const next = prev.includes(host)
        ? prev.filter(h => h !== host)
        : [...prev, host].slice(0, MAX_PINNED);
      try {
        localStorage.setItem(PINNED_KEY, JSON.stringify(next));
      } catch { /* quota exceeded — keep in-memory value */ }
      // Notify other consumers (HomeOverview, etc.) in the same tab.
      try { window.dispatchEvent(new CustomEvent(SYNC_EVENT)); } catch { /* ignore */ }
      return next;
    });
  }, []);

  return { pinned, togglePin, isPinned: (host: string) => pinned.includes(host) };
}
